import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { pageHead } from "@/lib/seo";




export const Route = createFileRoute("/login")({
  head: () => pageHead({
    path: "/login",
    title: "Sign in — Book Swap",
    description: "Sign in to Book Swap to message neighbors, manage your listings and follow your book exchanges.",
  }),
  component: Login,
});

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [showPw, setShowPw] = useState(false);



  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!agree) {
      toast.error("Please agree to the Terms of Use and Privacy Policy.");
      return;
    }
    setError(null);

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    setLoading(false);
    if (error) {
      const raw = `${error.message} ${(error as { code?: string }).code ?? ""}`.toLowerCase();
      const msg = raw.includes("invalid login") || raw.includes("invalid_credentials")
        ? "Incorrect email or password. Check for extra spaces or caps lock, or reset your password."
        : raw.includes("not confirmed") || raw.includes("email_not_confirmed")
          ? "Your email isn't verified yet. Check your inbox for the confirmation link."
          : raw.includes("rate") || raw.includes("too many") || raw.includes("429")
            ? "Too many attempts. Please wait a minute and try again."
            : raw.includes("network") || raw.includes("fetch")
              ? "Network problem — check your connection and try again."
              : error.message || "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
      return;
    }

    toast.success("Welcome back!");
    navigate({ to: "/browse" });
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="rounded-2xl bg-card p-8 shadow-[var(--shadow-card)]">
          <h1 className="text-2xl font-bold">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to browse books near you.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/reset-password" className="text-xs font-medium text-primary hover:underline">Forgot password?</Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3">
              <Checkbox checked={agree} onCheckedChange={(v) => setAgree(v === true)} className="mt-0.5" />
              <span className="text-sm text-muted-foreground">
                I agree to the{" "}
                <Link to="/terms" className="font-medium text-primary hover:underline">Terms of Use</Link>
                {" "}and{" "}
                <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
              </span>
            </label>
            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading || !agree}>
              {loading ? "Signing in…" : "Sign in"}
            </Button>

          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            New here? <Link to="/register" className="font-medium text-primary hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
